import React, { createContext, useEffect, useState, ReactNode } from "react";

interface EmbedContextValue {
  isEmbedded: boolean;
}

export const EmbedContext = createContext<EmbedContextValue>({
  isEmbedded: false,
});

const EMBED_STORAGE_KEY = "laborhack_embed_mode";

interface EmbedProviderProps {
  children: ReactNode;
}

export const EmbedProvider: React.FC<EmbedProviderProps> = ({ children }) => {
  const [isEmbedded, setIsEmbedded] = useState<boolean>(() => {
    // Check sessionStorage first for persisted state
    const storedValue = sessionStorage.getItem(EMBED_STORAGE_KEY);
    if (storedValue === "true") {
      return true;
    }

    // Check URL for embed=true parameter
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("embed") === "true";
  });

  useEffect(() => {
    // Check URL on mount and when location changes
    const urlParams = new URLSearchParams(window.location.search);
    const embedParam = urlParams.get("embed") === "true";

    if (embedParam) {
      setIsEmbedded(true);
      sessionStorage.setItem(EMBED_STORAGE_KEY, "true");
    }
  }, []);

  // Apply embed-specific styles (white background, no border)
  useEffect(() => {
    if (isEmbedded) {
      // Use setProperty with 'important' to override !important in CSS
      document.body.style.setProperty("background-color", "#ffffff", "important");
      document.body.style.setProperty("border", "none", "important");
      document.documentElement.style.setProperty("background-color", "#ffffff", "important");
      document.documentElement.style.setProperty("border", "none", "important");
    }

    return () => {
      // Cleanup on unmount (though unlikely in embed mode)
      if (isEmbedded) {
        document.body.style.removeProperty("background-color");
        document.body.style.removeProperty("border");
        document.documentElement.style.removeProperty("background-color");
        document.documentElement.style.removeProperty("border");
      }
    };
  }, [isEmbedded]);

  return (
    <EmbedContext.Provider value={{ isEmbedded }}>
      {children}
    </EmbedContext.Provider>
  );
};
